import { useEffect, useRef } from "react";

/**
 * Generates a phone-style ringtone or ringback tone using the Web Audio API.
 * - "incoming": Plays a repeating two-burst ring (like a phone ringing)
 * - "ringing":  Plays a standard ringback tone (1s on, 3s off)
 */
const useCallTone = (callState: "idle" | "ringing" | "active" | "incoming") => {
  const audioCtxRef = useRef<AudioContext | null>(null);
  const stopRef = useRef(false);

  useEffect(() => {
    if (callState !== "incoming" && callState !== "ringing") {
      // Stop any playing tone
      audioCtxRef.current?.close();
      audioCtxRef.current = null;
      return () => {};
    }

    stopRef.current = false;
    const ctx = new AudioContext();
    audioCtxRef.current = ctx;

    const playTone = (
      frequency: number,
      startTime: number,
      duration: number,
      gainValue = 0.3,
    ) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, startTime);

      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(gainValue, startTime + 0.01);
      gainNode.gain.setValueAtTime(gainValue, startTime + duration - 0.01);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };

    const scheduleIncomingRing = () => {
      // Quick "drr-drr" style: 0.4s on, 0.1s gap, 0.4s on, 0.6s silence = 1.5s cycle
      const ringInterval = 1.5;
      const burst1Duration = 0.4;
      const burst2Duration = 0.4;
      const burst2Offset = 0.5; // very short gap between bursts

      let cycle = 0;

      const scheduleCycle = () => {
        if (stopRef.current || ctx.state === "closed") return;

        const baseTime = ctx.currentTime + cycle * ringInterval;
        playTone(880, baseTime, burst1Duration, 0.25);
        playTone(1080, baseTime, burst1Duration, 0.12);
        playTone(880, baseTime + burst2Offset, burst2Duration, 0.25);
        playTone(1080, baseTime + burst2Offset, burst2Duration, 0.12);

        cycle += 1;
        const delay = (ringInterval - 0.1) * 1000;
        setTimeout(scheduleCycle, delay);
      };

      scheduleCycle();
    };

    const scheduleRingback = () => {
      // Tighter ringback: 0.8s on, 0.8s silence = 1.6s cycle (less dead air)
      const onDuration = 0.8;
      const cycleLength = 1.6;
      let cycle = 0;

      const scheduleCycle = () => {
        if (stopRef.current || ctx.state === "closed") return;

        const baseTime = ctx.currentTime + cycle * cycleLength;
        playTone(440, baseTime, onDuration, 0.18);
        playTone(480, baseTime, onDuration, 0.12);

        cycle += 1;
        const delay = (cycleLength - 0.1) * 1000;
        setTimeout(scheduleCycle, delay);
      };

      scheduleCycle();
    };

    if (callState === "incoming") {
      scheduleIncomingRing();
    } else if (callState === "ringing") {
      scheduleRingback();
    }

    return () => {
      stopRef.current = true;
      ctx.close();
      audioCtxRef.current = null;
    };
  }, [callState]);
};

export default useCallTone;
